import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import { InstantSearch, Configure } from "react-instantsearch";
import { TabbedSupplierList } from "./TabbedSupplierList";
import { SelectedSuppliersPanel } from "./SelectedSuppliersPanel";
import { PartsBreakdownPanel } from "./PartsBreakdownPanel";
import { BroadcastSuppliersPanel } from "./BroadcastSuppliersPanel";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";
import {
  useGetAllSuppliers,
  useGetSupplierInventoryDeltas,
  useGetSupplierPartAvailability,
} from "../../views/Orders/queryHooks";
import { buildOfflineSupplierMap } from "./utils/offlinePartAvailabilityUtils";
import { buildSupplierInventoryDeltaQuantityMap } from "./utils/supplierInventoryDeltaUtils";
import {
  buildSupplierPartConfirmationQuantityMap,
  overlayDeclinedPartConfirmationsOnQuantityMap,
  overlayDiscoveryBucketReservations,
} from "./utils/supplierPartConfirmationUtils";
import { buildInventoryAvailableByRootPartFromTypesense } from "./utils/buildInventoryAvailableByRootPart";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  IconButton,
  VStack,
  HStack,
  Tag,
  Link,
} from "@chakra-ui/react";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import RadiusFilter from "./GeoLocationFilter";
import GaraazInfoDialogue from "../GaraazInfoDialogue";
import TransactionTypeSelectComponent from "../TransactionTypeSelectComponent";
import {
  DeliveryOptionsSupplierInput,
  useDeliveryOptionsLauncher,
  buildPartsPayloadForDeliveryOptions,
} from "../DeliveryOptions";
import { buildFamilySupplierOptionsForDelivery } from "../DeliveryOptions/utils/buildFamilySupplierOptionsForDelivery";
import { useOrdersStore } from "../../views/Orders/store/useOrdersStore";
import { FaSearch } from "react-icons/fa";
import services from "../../views/Orders/services";
import { useToast } from "@chakra-ui/react";
import { OrderStatus, OrdersTextLabels, ORDER } from "../../views/Orders/enums";
import { META_INFO_KEYS } from "../../utils/constants";
import { getCurrentUTCTimestamp } from "../../utils/dateResolvers";
import PartSelectionModal from "../PartSelectionModal/PartSelectionModal";
import {
  PartConfirmationBucketEntry,
  PartQuantity,
  SelectedSupplier,
  calculateRemainingParts,
} from "./utils/partsCalculationUtils";
import { isPartConfirmationEligibleSupplier } from "./utils/partConfirmationEligibility";
import {
  AddToSupplierBucketOptions,
  buildConfirmedByPartId,
  buildDefaultSelectedPartsForBucket,
  buildDiscoveryExcludedSupplierIdsFromPartConfirmations,
} from "./utils/assignQtyForSupplierParts";
import {
  canGenerateEstimateWithPartConfirmation,
  hydrateDualBucketsFromPartConfirmations,
  buildPartConfirmationCommittedSuppliers,
  buildPartConfirmationEntryFromPendingResponses,
  getSendablePartConfirmationEntries,
  mergePartConfirmationBucketEntries,
  reconcilePartConfirmationBucketEntry,
} from "./utils/partConfirmationBucketUtils";
import { buildPartConfirmationBucketEntry } from "./utils/buildPartConfirmationBucketEntry";
import { sanitizeSplitData } from "./utils/sanitizeSplitData";
import {
  downloadBucketAvailabilityPdf,
  enrichPartQuantitiesWithOrderMrp,
} from "./utils/downloadBucketAvailabilityPdf";
import { IBucket } from "../../views/Orders/store/useBucketsStore";
import { BucketType } from "./types";
import { parseNonNegativeWholeNumber } from "./utils/util";
import { useBucketMethods } from "./utils/bucketUtils";
import GaraazLoadingModal from "./GaraazLoadingModal";
import { useSelectedSuppliers } from "./hooks/useSelectedSuppliers";
import { usePartConfirmationBucket } from "./hooks/usePartConfirmationBucket";
import {
  useSendPartConfirmationBucket,
  PartConfirmationSendResult,
} from "../../views/Orders/hooks/useSendPartConfirmationBucket";
import { useListPartConfirmationsForOrder } from "../../views/Orders/queryHooks";
import { useBroadcastSuppliers } from "./hooks/useBroadcastSuppliers";
import {
  sendBroadcastQuery,
  getNotificationRequestStatus,
  buildBroadcastRequestId,
  SuprsendPerEntityStatus,
  BROADCAST_STATUS,
} from "./utils/broadcastUtils";
import { computeDelayFromNow } from "../../utils/util";
import { sanitizePhoneNumber, formatMobileNumber } from "../../utils/util";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
  DeliveryOptionsLaunchConfig,
  DeliverySupplierOption,
  EstimateSplitDeliveryType,
} from "../DeliveryOptions/types";
import {
  useOrderLabel,
  GetOrderLabels,
} from "../../views/Orders/components/GaraazOrderCard/OrderLabelContext";
import type { EstimateSplitMode } from "../../views/Orders/services/EstimateAllocatorService/types";
import {
  formatSupplierAssociationsForDisplay,
  SUPPLIER_ASSOCIATION_DISPLAY_FIELDS,
} from "../../utils/supplierAssociationUtils";

const createPhoneLink = (phone?: string | null) => {
  const sanitized = sanitizePhoneNumber(phone);
  const formatted = formatMobileNumber(phone);

  if (!sanitized || !formatted) return undefined;

  return (
    <Link
      href={`tel:${sanitized}`}
      color="blue.600"
      fontWeight="700"
      fontSize="0.95rem"
    >
      {formatted}
    </Link>
  );
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

enum ValidationErrorType {
  DUPLICATE_PARTS = "DUPLICATE_PARTS",
  PARTS_LIMIT_EXCEEDED = "PARTS_LIMIT_EXCEEDED",
}

interface ValidationError {
  type: ValidationErrorType;
  message: string;
}

interface SupplierInventorySearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectionClose: () => void;
  preFacetedPartNumbers: PartQuantity[];
  customerGeoPoint: { latitude: number; longitude: number };
  handleSelectListItem: (
    supplier: any,
    supplierParts: any[],
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    selections?: NormalizedDeliverySelection[],
    cptGivenTime?: string,
    splitData?: EnrichedSupplierForDelivery[],
    estimateNo?: string,
    estimateSplitMode?: EstimateSplitMode,
  ) => void | Promise<void>;
  handleExecuteSupplierBucket: (
    supplierBucket: any,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    splitSelections?: NormalizedDeliverySelection[],
    cptGivenTime?: string,
    splitData?: EnrichedSupplierForDelivery[],
    estimateNo?: string,
    estimateSplitMode?: EstimateSplitMode,
  ) => Promise<any>;
  isSupplierBucketExecuting: boolean;
  associations: any[];
  orderId: string;
  orderNumber: string;
  setTransactionType: (type: string) => void;
  orderData: any;
  allSuppliers?: any[];
  filteredSuppliers?: any[];
  refetchAllOrders?: () => void;
  estimateSplitMode?: EstimateSplitMode;
}

const SupplierInventorySearch: React.FC<SupplierInventorySearchProps> = ({
  isOpen,
  onClose,
  onSelectionClose,
  preFacetedPartNumbers,
  customerGeoPoint,
  handleSelectListItem,
  handleExecuteSupplierBucket,
  isSupplierBucketExecuting,
  associations,
  orderId,
  orderNumber,
  setTransactionType,
  orderData,
  allSuppliers: allSuppliersFromProps = [],
  filteredSuppliers = [],
  refetchAllOrders,
  estimateSplitMode = "assign",
}) => {
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const queryClient = useQueryClient();
  const { data: allSuppliersFromQuery } = useGetAllSuppliers();
  const { orderLabel }: { orderLabel: GetOrderLabels } = useOrderLabel();

  // Order cancellation reasons from config
  const orderCancellationReasons = useMemo(() => {
    try {
      return mobileConfigs?.order_cancellation_reasons
        ? JSON.parse(mobileConfigs.order_cancellation_reasons)
        : [];
    } catch {
      return [];
    }
  }, [mobileConfigs?.order_cancellation_reasons]);

  const allSuppliers = useMemo(
    () =>
      allSuppliersFromProps.length > 0
        ? allSuppliersFromProps
        : allSuppliersFromQuery || [],
    [allSuppliersFromProps, allSuppliersFromQuery],
  );
  const [customerSupplierAssociations, setCustomerSupplierAssociations] =
    useState<string[]>([]);

  const buildDeliverySupplierOptions = useCallback(
    (supplierId?: string | null): DeliverySupplierOption[] | undefined => {
      if (!supplierId) {
        return undefined;
      }

      const options = buildFamilySupplierOptionsForDelivery({
        splitSupplierId: supplierId,
        orderData,
        allSuppliers,
        associateIds: customerSupplierAssociations,
      });

      return options.length > 0 ? options : undefined;
    },
    [allSuppliers, customerSupplierAssociations, orderData],
  );

  const supplierCityMap = useMemo(() => {
    const map = new Map<string, string>();
    if (allSuppliers && Array.isArray(allSuppliers)) {
      allSuppliers.forEach((supplier: any) => {
        if (supplier._id) {
          const city = supplier.address?.city || supplier.supplierAddress?.city;
          if (city) {
            map.set(supplier._id, city);
          }
        }
      });
    }
    return map;
  }, [allSuppliers]);

  const {
    partConfirmationBucket,
    addSupplierToPartConfirmationBucket,
    removeSupplierFromPartConfirmationBucket,
    clearPartConfirmationBucket,
    setPartConfirmationBucket,
  } = usePartConfirmationBucket();

  const sendPartConfirmationBucket = useSendPartConfirmationBucket();
  const [partConfirmationSendResults, setPartConfirmationSendResults] =
    useState<PartConfirmationSendResult[] | undefined>(undefined);
  const partConfirmationOrderNo = orderNumber || orderData?.orderNo || "";
  const {
    data: partConfirmationsForOrder,
    isFetched: isPartConfirmationsFetched,
  } = useListPartConfirmationsForOrder(partConfirmationOrderNo, isOpen);
  const [hasHydratedPartConfirmations, setHasHydratedPartConfirmations] =
    useState(false);

  const partConfirmationResponsesBySupplierId = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        partId?: string;
        partNumber?: string;
        rootPartNumber?: string;
        askedQty?: number;
        responseStatus?: string;
        confirmedQty?: number | null;
        status?: string;
        quantity?: number;
      }>
    >();
    for (const supplier of partConfirmationsForOrder?.suppliers ?? []) {
      const supplierId = String(supplier.supplierId ?? "").trim();
      if (!supplierId) continue;
      map.set(supplierId, [
        ...(map.get(supplierId) ?? []),
        ...(supplier.parts ?? []),
      ]);
    }
    return map;
  }, [partConfirmationsForOrder]);

  const partConfirmationCommittedSuppliers = useMemo(
    () =>
      buildPartConfirmationCommittedSuppliers(
        partConfirmationResponsesBySupplierId,
        partConfirmationBucket,
      ),
    [partConfirmationResponsesBySupplierId, partConfirmationBucket],
  );

  const sendablePartConfirmationEntries = useMemo(
    () =>
      getSendablePartConfirmationEntries(
        partConfirmationBucket,
        partConfirmationResponsesBySupplierId,
      ),
    [partConfirmationBucket, partConfirmationResponsesBySupplierId],
  );

  const {
    selectedSuppliersInBucket,
    remainingPartsToFulfill,
    isOrderFullyFulfilled,
    orderFulfillmentPercentage,
    addSupplierToSelectionBucket,
    removeSupplierFromSelectionBucket,
    reducePartQuantityFromSupplierInBucket,
    clearAllSuppliersFromSelectionBucket,
  } = useSelectedSuppliers(
    preFacetedPartNumbers,
    partConfirmationCommittedSuppliers,
  );

  const requestId = orderNumber || orderData?.orderNo;
  const broadcastRequestId = useMemo(() => {
    const baseOrderNo = orderNumber || orderData?.orderNo;
    return baseOrderNo ? buildBroadcastRequestId(baseOrderNo) : undefined;
  }, [orderNumber, orderData?.orderNo]);
  const partIdsFromRemaining = useMemo(
    () =>
      remainingPartsToFulfill
        .map((p) => p.partId)
        .filter((id): id is string => !!id),
    [remainingPartsToFulfill],
  );
  const uniqueRootPartNumbers = useMemo(
    () =>
      Array.from(
        new Set(remainingPartsToFulfill.map((part) => part.rootPartNumber)),
      ),
    [remainingPartsToFulfill],
  );
  const remainingDiscoverySignature = useMemo(
    () =>
      remainingPartsToFulfill
        .map((part) => `${part.rootPartNumber}:${part.quantity}`)
        .join("|"),
    [remainingPartsToFulfill],
  );
  const { data: supplierPartAvailabilityResponse } =
    useGetSupplierPartAvailability(requestId, isOpen, partIdsFromRemaining);
  const { data: supplierInventoryDeltaResponse } =
    useGetSupplierInventoryDeltas(isOpen, uniqueRootPartNumbers);

  const supplierInventoryDeltaQuantityMap = useMemo(
    () =>
      buildSupplierInventoryDeltaQuantityMap(supplierInventoryDeltaResponse),
    [supplierInventoryDeltaResponse],
  );
  // Hide suppliers whose PC responses leave no selectable remaining qty
  // (e.g. all declined). Do not touch effective-qty overlay math below.
  const discoveryExcludedSupplierIds = useMemo(
    () =>
      buildDiscoveryExcludedSupplierIdsFromPartConfirmations(
        partConfirmationResponsesBySupplierId,
        remainingPartsToFulfill,
      ),
    [partConfirmationResponsesBySupplierId, remainingPartsToFulfill],
  );

  const supplierPartConfirmationQuantityMapFromApi = useMemo(
    () =>
      buildSupplierPartConfirmationQuantityMap(
        supplierInventoryDeltaResponse,
      ),
    [supplierInventoryDeltaResponse],
  );

  // Discovery metrics only: bucket + declined overlays. Do NOT use this map when
  // stamping confirm-order inventoryItems (that must stay API effective qty).
  const supplierPartConfirmationQuantityMap = useMemo(
    () =>
      overlayDeclinedPartConfirmationsOnQuantityMap(
        overlayDiscoveryBucketReservations(
          supplierPartConfirmationQuantityMapFromApi,
          selectedSuppliersInBucket,
          partConfirmationBucket,
        ),
        partConfirmationResponsesBySupplierId,
        preFacetedPartNumbers,
      ),
    [
      supplierPartConfirmationQuantityMapFromApi,
      selectedSuppliersInBucket,
      partConfirmationBucket,
      partConfirmationResponsesBySupplierId,
      preFacetedPartNumbers,
    ],
  );

  const offlinePartAvailabilityDiscrepanciesPerSupplierMap = useMemo(() => {
    return buildOfflineSupplierMap(
      supplierPartAvailabilityResponse,
      allSuppliers,
      remainingPartsToFulfill,
      discoveryExcludedSupplierIds,
    );
  }, [
    supplierPartAvailabilityResponse,
    allSuppliers,
    remainingPartsToFulfill,
    discoveryExcludedSupplierIds,
  ]);

  const [maxKm, setMaxKm] = useState<any>("");
  const [geoDistanceFilter, setGeoDistanceFilter] = useState("");
  const supplierDiscoveryCollectionName = "supplierinventories_rpn_v2";

  const supplier_discovery_suppliers_limit = Number(
    mobileConfigs?.supplier_discovery_suppliers_limit ?? 250,
  );

  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [selectedSupplierParts, setSelectedSupplierParts] = useState<any>(null);
  const [isTransactionTypeDialogOpen, setIsTransactionTypeDialogOpen] =
    useState(false);
  const [isSupplierAllocationInProgress, setIsSupplierAllocationInProgress] =
    useState(false);
  const {
    deliveryOptionsModal: DeliveryOptionsModal,
    launchDeliveryOptionsModal,
    isFetchingDeliveryOptions,
    estimateNo,
    executeShareEstimate,
  } = useDeliveryOptionsLauncher();
  const setOrderMetaInfoWithOrderId = useOrdersStore(
    (state) => state.setOrderMetaInfoWithOrderId,
  );
  const orderItemsForMrpEnrichment = useOrdersStore(
    useCallback((state) => state.orderItems[orderId], [orderId]),
  );

  const restoreUnassignedPartsToRemainingBucket = useCallback(
    (
      parts: Array<{
        partId?: string;
        partNumber: string;
        quantity: number;
        sourceSupplierId: string;
      }>,
    ) => {
      for (const part of parts) {
        const orderItems =
          orderItemsForMrpEnrichment || orderData?.orderItems || [];
        const orderItem = orderItems.find(
          (item: {
            partId?: string;
            rootPartNumber?: string;
            partNumber?: string;
          }) =>
            (part.partId && String(item.partId) === String(part.partId)) ||
            item.rootPartNumber === part.partNumber ||
            item.partNumber === part.partNumber,
        );
        reducePartQuantityFromSupplierInBucket(
          part.sourceSupplierId,
          {
            rootPartNumber: orderItem?.rootPartNumber || part.partNumber,
            partNumber: orderItem?.partNumber || part.partNumber,
            partId: part.partId,
          },
          part.quantity,
        );
      }
    },
    [
      orderItemsForMrpEnrichment,
      orderData?.orderItems,
      reducePartQuantityFromSupplierInBucket,
    ],
  );
  const restoreUnassignedPartsToRemainingBucketRef = React.useRef(
    restoreUnassignedPartsToRemainingBucket,
  );
  restoreUnassignedPartsToRemainingBucketRef.current =
    restoreUnassignedPartsToRemainingBucket;
  const onPartsUnassignedFromEstimate = useCallback(
    (
      parts: Array<{
        partId?: string;
        partNumber: string;
        quantity: number;
        sourceSupplierId: string;
      }>,
    ) => {
      restoreUnassignedPartsToRemainingBucketRef.current(parts);
    },
    [],
  );

  const childOrdersForEstimateLaunch = useOrdersStore((state) => {
    const enquiryId = orderData?._id ?? orderId;
    if (!enquiryId) {
      return undefined;
    }
    const rows = state.childOrders.get(enquiryId);
    if (rows == null) {
      return undefined;
    }
    return rows.map((child: { isDeleted?: boolean; orderStatus?: string }) => ({
      isDeleted: child.isDeleted,
      orderStatus: child.orderStatus,
    }));
  });

  const [showCreateAnotherBucket, setShowCreateAnotherBucket] =
    useState<boolean>(false);
  const [selectedBucket, setSelectedBucket] = useState<
    SelectedSupplier[] | null
  >(null);
  const [isExecuteBucketClicked, setIsExecuteBucketClicked] = useState(false);
  const [isShareEstimateFlow, setIsShareEstimateFlow] = useState(false);
  // Estimate modal: transaction type configured via PATCH split (MultiSplitTransactionTypeModal retained but unused)
  // const [
  //   isMultiSplitTransactionTypeModalOpen,
  //   setIsMultiSplitTransactionTypeModalOpen,
  // ] = useState(false);
  // const [
  //   multiSplitTransactionTypeModalSuppliers,
  //   setMultiSplitTransactionTypeModalSuppliers,
  // ] = useState<DeliveryOptionsSupplierInput[]>([]);
  // const [deliveryOptionsLaunchPayload, setDeliveryOptionsLaunchPayload] =
  //   useState<DeliveryOptionsLaunchConfig | null>(null);
  // const handleMultiSplitTransactionTypeModalConfirm = useCallback(
  //   (
  //     supplierSplitTransactionTypeConfigs: SupplierSplitTransactionTypeConfig[],
  //   ) => {
  //     setIsMultiSplitTransactionTypeModalOpen(false);
  //     if (deliveryOptionsLaunchPayload) {
  //       launchDeliveryOptionsModal({
  //         ...deliveryOptionsLaunchPayload,
  //         supplierSplitTransactionTypeConfigs,
  //       });
  //       setDeliveryOptionsLaunchPayload(null);
  //     }
  //   },
  //   [deliveryOptionsLaunchPayload, launchDeliveryOptionsModal],
  // );
  // const handleMultiSplitTransactionTypeModalCancel = useCallback(() => {
  //   setIsMultiSplitTransactionTypeModalOpen(false);
  //   setDeliveryOptionsLaunchPayload(null);
  // }, []);

  const [storedSelections, setStoredSelections] = useState<
    NormalizedDeliverySelection[]
  >([]);
  const [storedSplitData, setStoredSplitData] = useState<
    EnrichedSupplierForDelivery[]
  >([]);
  const [cptGivenTime, setCptGivenTime] = useState<string>("");

  const persistSanitizedDeliveryConfirm = useCallback(
    (
      selections: NormalizedDeliverySelection[],
      splitData: EnrichedSupplierForDelivery[],
    ): EnrichedSupplierForDelivery[] => {
      const sanitized = sanitizeSplitData(splitData || []);
      setStoredSelections(selections);
      setStoredSplitData(sanitized);
      return sanitized;
    },
    [],
  );

  const [manualListSearch, setManualListSearch] = useState("");
  const [isPartSelectionModalOpen, setIsPartSelectionModalOpen] =
    useState(false);
  const [selectedManualSupplier, setSelectedManualSupplier] =
    useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  const supplier_discovery_parts_limit = Number(
    mobileConfigs?.supplier_discovery_parts_limit ?? 99,
  );

  const supplier_discovery_max_buckets = parseNonNegativeWholeNumber(
    (mobileConfigs as any)?.allowed_number_of_buckets_in_supplier_discovery,
  );

  // Broadcast suppliers state management
  const {
    broadcastSuppliers,
    addSupplierToBroadcast,
    removeSupplierFromBroadcast,
    clearBroadcastSuppliers,
    isSupplierInBroadcastList,
  } = useBroadcastSuppliers();

  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const [
    notificationRequestStatusBySupplier,
    setnotificationRequestStatusBySupplier,
  ] = useState<Record<string, SuprsendPerEntityStatus>>({});

  // Initialize toast for broadcast notifications (must be before broadcast functions)
  const toast = useToast();

  // Get max broadcast limit from config
  const max_broadcast_suppliers_limit = Number(
    mobileConfigs?.max_broadcast_suppliers_limit ?? 5,
  );

  const isBroadcastEnabled =
    mobileConfigs?.ff_offline_discovery_broadcast_enabled_orca === "true" ||
    mobileConfigs?.ff_offline_discovery_broadcast_enabled_orca === true;

  const refreshSuprsendStatuses = useCallback(async () => {
    if (!broadcastRequestId) return;
    try {
      const status = await getNotificationRequestStatus(broadcastRequestId);
      if (!status || !status?.perEntityStatus) {
        setnotificationRequestStatusBySupplier({});
        return;
      }

      setnotificationRequestStatusBySupplier(status?.perEntityStatus);
    } catch (error) {
      console.error("Failed to refresh suprsend statuses", error);
    }
  }, [broadcastRequestId]);

  useEffect(() => {
    if (isOpen && broadcastRequestId) {
      void refreshSuprsendStatuses();
    }
  }, [isOpen, broadcastRequestId, refreshSuprsendStatuses]);

  // Handle send WhatsApp broadcast query
  const handleSendBroadcastQuery = useCallback(async () => {
    if (!isBroadcastEnabled) return;
    setIsSendingBroadcast(true);
    const optimisticStatuses: Record<string, SuprsendPerEntityStatus> = {};
    broadcastSuppliers.forEach((supplier) => {
      optimisticStatuses[supplier.supplierId] = {
        sentAt: new Date().toISOString(),
        respondedAt: null,
        expiresAt: null,
        status: BROADCAST_STATUS.SENT,
      };
    });
    setnotificationRequestStatusBySupplier((prev) => ({
      ...prev,
      ...optimisticStatuses,
    }));

    try {
      await sendBroadcastQuery(
        broadcastSuppliers,
        toast,
        orderNumber,
        mobileConfigs,
      );
      await refreshSuprsendStatuses();
      clearBroadcastSuppliers();
    } catch (error) {
      setnotificationRequestStatusBySupplier((prev) => {
        const updated = { ...prev };
        broadcastSuppliers.forEach((supplier) => {
          delete updated[supplier.supplierId];
        });
        return updated;
      });
    } finally {
      setIsSendingBroadcast(false);
    }
  }, [
    broadcastSuppliers,
    clearBroadcastSuppliers,
    isBroadcastEnabled,
    mobileConfigs,
    orderNumber,
    refreshSuprsendStatuses,
    toast,
  ]);

  const handleAddToBroadcast = useCallback(
    (supplier: any) => {
      if (!isBroadcastEnabled || isSendingBroadcast) return;

      const supplierStatus =
        notificationRequestStatusBySupplier?.[supplier._id];
      if (supplierStatus?.sentAt) {
        toast({
          title: "Already Sent",
          description: `Broadcast already sent to ${supplier.businessName || "this supplier"}.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      if (broadcastSuppliers.length >= max_broadcast_suppliers_limit) {
        toast({
          title: "Broadcast List Full",
          description: `You can only add up to ${max_broadcast_suppliers_limit} suppliers to the broadcast list.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      if (!supplier.user?.mobileNumber) {
        toast({
          title: "No Mobile Number",
          description: `Supplier ${supplier.businessName || supplier.name} does not have a mobile number.`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      addSupplierToBroadcast(supplier);
    },
    [
      addSupplierToBroadcast,
      broadcastSuppliers.length,
      isBroadcastEnabled,
      isSendingBroadcast,
      notificationRequestStatusBySupplier,
      max_broadcast_suppliers_limit,
      toast,
    ],
  );

  const { getBucketCountByOrderNumber, saveCurrentSelectionAsBucket } =
    useBucketMethods();

  const savedBucketCount = getBucketCountByOrderNumber(orderNumber);

  const isBucketSaveAllowed =
    selectedSuppliersInBucket.length > 0 &&
    savedBucketCount < supplier_discovery_max_buckets;

  const handleSaveCurrentBucket = React.useCallback(() => {
    if (!isBucketSaveAllowed) return;
    saveCurrentSelectionAsBucket(
      orderNumber,
      selectedSuppliersInBucket,
      preFacetedPartNumbers,
    );
    clearAllSuppliersFromSelectionBucket();
  }, [
    isBucketSaveAllowed,
    orderNumber,
    selectedSuppliersInBucket,
    preFacetedPartNumbers,
    clearAllSuppliersFromSelectionBucket,
    saveCurrentSelectionAsBucket,
  ]);

  useEffect(() => {
    return () => {
      clearBroadcastSuppliers();
    };
  }, [clearBroadcastSuppliers]);

  useEffect(() => {
    if (!isBroadcastEnabled) {
      clearBroadcastSuppliers();
    }
  }, [isBroadcastEnabled, clearBroadcastSuppliers]);

  useEffect(() => {
    if (isOpen) {
      clearAllSuppliersFromSelectionBucket();
      clearPartConfirmationBucket();
      setPartConfirmationSendResults(undefined);
      clearBroadcastSuppliers();
      setManualListSearch("");
      setActiveTab(0);
      setHasHydratedPartConfirmations(false);
    }
  }, [
    isOpen,
    clearAllSuppliersFromSelectionBucket,
    clearPartConfirmationBucket,
    clearBroadcastSuppliers,
  ]);

  const validationErrors = useMemo(() => {
    const errors: ValidationError[] = [];

    const rootPartNumberMap = new Map<string, Set<string>>();
    preFacetedPartNumbers.forEach((part: any) => {
      const root = part.rootPartNumber;
      if (!root) return;
      if (!rootPartNumberMap.has(root)) {
        rootPartNumberMap.set(root, new Set());
      }
      rootPartNumberMap.get(root)!.add(part.partNumber || root);
    });

    const duplicateGroups = Array.from(rootPartNumberMap.entries()).filter(
      ([_, partNumbers]) => partNumbers.size > 1,
    );

    if (duplicateGroups.length > 0) {
      const dupDetails = duplicateGroups
        .map(([root, partNumbers]) => {
          return `\u2022 ${root}: [${Array.from(partNumbers).join(", ")}]`;
        })
        .join("\n");

      errors.push({
        type: ValidationErrorType.DUPLICATE_PARTS,
        message:
          "Supplier discovery is not supported for part numbers belonging to the same part family.\n\nPlease select only one part number from each family:\n" +
          dupDetails,
      });
    }

    if (preFacetedPartNumbers.length > supplier_discovery_parts_limit) {
      errors.push({
        type: ValidationErrorType.PARTS_LIMIT_EXCEEDED,
        message: `Number of parts (${preFacetedPartNumbers.length}) exceeds the maximum limit of ${supplier_discovery_parts_limit}.`,
      });
    }

    return errors;
  }, [preFacetedPartNumbers, supplier_discovery_parts_limit]);

  const buildDeliveryOptionsSuppliersPayload = useCallback(
    (selectedSuppliers: SelectedSupplier[]): DeliveryOptionsSupplierInput[] => {
      // Initialize remaining map and order item map from preFacetedPartNumbers
      const remainingByPart = new Map<string, number>();
      const orderItemMap = new Map<string, any>();
      (preFacetedPartNumbers || []).forEach((item: any) => {
        const key = item.rootPartNumber;
        if (key) {
          remainingByPart.set(key, item.quantity || 0);
          orderItemMap.set(key, item);
        }
      });

      const orderItemsForPayload = (preFacetedPartNumbers || []).map((p) => {
        return {
          partId: p.partId ?? "",
          rootPartNumber: p.rootPartNumber,
          partNumber: p.partNumber,
          orderedQuantity: p.quantity,
          partName: p.partName ?? "",
          mrp: p.mrp,
        };
      });

      const suppliersPayload: DeliveryOptionsSupplierInput[] =
        selectedSuppliers.map((supplier) => {
          const clampedParts = supplier.selectedParts
            .map((part) => {
              const remaining = remainingByPart.get(part.rootPartNumber) ?? 0;
              const availableQuantity = Math.max(
                0,
                Number(part.familyQuantity) || 0,
              );
              const qty = Math.min(remaining, availableQuantity);

              // Update remaining for next supplier
              if (qty > 0) {
                remainingByPart.set(part.rootPartNumber, remaining - qty);
              }

              if (qty <= 0) return null;
              return {
                rootPartNumber: part.rootPartNumber,
                allocatedQuantity: qty,
                availableQuantity,
                source: part.source,
              };
            })
            .filter(Boolean) as {
            rootPartNumber: string;
            allocatedQuantity: number;
            availableQuantity: number;
            source?: string;
          }[];

          const items = buildPartsPayloadForDeliveryOptions(
            clampedParts,
            orderItemsForPayload,
          );

          // Snapshot supplier availability for estimate/order inventoryItems.
          // Prefer Typesense stock map when present; else allocation familyQuantity.
          // Only stamp parts actually allocated to this supplier.
          const availableQtyByRootPart = new Map<string, number>();
          if (supplier.inventoryAvailableByRootPart) {
            for (const [root, qty] of Object.entries(
              supplier.inventoryAvailableByRootPart,
            )) {
              availableQtyByRootPart.set(root, Math.max(0, Number(qty) || 0));
            }
          } else {
            for (const part of clampedParts) {
              availableQtyByRootPart.set(
                part.rootPartNumber,
                part.availableQuantity,
              );
            }
          }

          const allocatedRootPartNumbers = new Set(
            clampedParts.map((part) => part.rootPartNumber),
          );

          const inventoryItems = orderItemsForPayload
            .filter((orderItem) =>
              allocatedRootPartNumbers.has(orderItem.rootPartNumber),
            )
            .map((orderItem) => {
              if (!orderItem.partId) return null;
              return {
                partId: String(orderItem.partId),
                availableQuantity:
                  availableQtyByRootPart.get(orderItem.rootPartNumber) ?? 0,
              };
            })
            .filter(Boolean) as {
            partId: string;
            availableQuantity: number;
          }[];

          return {
            supplierId: supplier.supplierId,
            supplierName: supplier.supplierName,
            distanceKm: supplier.distanceKm,
            supplierCity: supplier.supplierCity,
            supplierOptions: buildDeliverySupplierOptions(supplier.supplierId),
            items,
            inventoryItems,
          };
        });

      // Compute remaining parts from remainingByPart and preFacetedPartNumbers
      const remainingParts: PartQuantity[] = [];
      remainingByPart.forEach((qty, rootPartNumber) => {
        if (qty > 0) {
          const orderItem = orderItemMap.get(rootPartNumber);
          if (orderItem) {
            remainingParts.push({
              rootPartNumber: orderItem.rootPartNumber,
              partNumber: orderItem.partNumber,
              quantity: qty,
              partName: orderItem.partName,
            });
          }
        }
      });

      // Append remaining items supplier if any
      if (remainingParts.length > 0) {
        const remainingItemsInput = remainingParts.map((p) => ({
          rootPartNumber: p.rootPartNumber,
          allocatedQuantity: p.quantity,
        }));

        const remainingItems = buildPartsPayloadForDeliveryOptions(
          remainingItemsInput,
          orderItemsForPayload,
        );

        suppliersPayload.push({
          supplierId: null, // null ID signals "Remaining Items"
          supplierName: "Supplier Not Found",
          items: remainingItems,
        });
      }

      return suppliersPayload;
    },
    [preFacetedPartNumbers, buildDeliverySupplierOptions],
  );

  const buildSupplierPayloadForDeliveryOptions = useCallback(
    (
      supplier: any,
      supplierParts: any[] = [],
    ): DeliveryOptionsSupplierInput[] => {
      const supplierCity = supplierCityMap.get(supplier?._id);

      // Construct a temporary SelectedSupplier object
      const tempSelectedSupplier: SelectedSupplier = {
        supplierId: supplier._id,
        supplierName: supplier.supplierName,
        selectedParts: supplierParts.map((p: any) => ({
          rootPartNumber: p.rootPartNumber,
          familyQuantity: p.familyQuantity,
          updatedAt: p.updatedAt,
          partName: p.partName,
          source: p.source,
        })),
        // Direct assign always comes from Auto Discovered list
        isFromOfflineDiscovery: false,
        selectedAt: new Date(),
        distanceKm: supplier.distanceKm,
        supplierCity: supplierCity,
      };

      // Unified helper computes remaining parts internally
      return buildDeliveryOptionsSuppliersPayload([tempSelectedSupplier]);
    },
    [supplierCityMap, buildDeliveryOptionsSuppliersPayload],
  );

  const generateOptimizedFilter = useMemo(() => {
    return (
      parts: PartQuantity[],
      geoFilter?: string,
      excludeSupplierIds?: string[],
    ) => {
      const partNumbers = parts.map((part) => part.rootPartNumber);
      const filterConditions = [`rootPartNumber:=[${partNumbers.join(",")}]`];

      if (geoFilter && geoFilter.trim()) {
        filterConditions.push(geoFilter);
      }

      if (excludeSupplierIds && excludeSupplierIds.length > 0) {
        const exclusionFilter = `supplierId:!=[${excludeSupplierIds.join(
          ",",
        )}]`;
        filterConditions.push(`${exclusionFilter}`);
      }

      return filterConditions.join(" && ");
    };
  }, []);

  const optimizedFilter = useMemo(() => {
    return generateOptimizedFilter(
      remainingPartsToFulfill,
      geoDistanceFilter,
      discoveryExcludedSupplierIds,
    );
  }, [
    remainingPartsToFulfill,
    geoDistanceFilter,
    generateOptimizedFilter,
    discoveryExcludedSupplierIds,
  ]);

  useEffect(() => {
    if (
      customerGeoPoint &&
      mobileConfigs?.default_supplier_discovery_distance_range_filter
    ) {
      const maxDistance = Number(
        mobileConfigs.default_supplier_discovery_distance_range_filter,
      );
      setMaxKm(maxDistance);
      setGeoDistanceFilter(
        `location:(${customerGeoPoint.latitude},${customerGeoPoint.longitude},${maxDistance} km)`,
      );
    }
  }, [
    customerGeoPoint,
    mobileConfigs?.default_supplier_discovery_distance_range_filter,
  ]);

  useEffect(() => {
    if (associations) {
      const customerSupplierAssociationsTemp = associations.map(
        (association: any) => association.supplierId,
      );
      setCustomerSupplierAssociations(customerSupplierAssociationsTemp);
    }
  }, [associations]);

  const typesenseInstantsearchAdapter = useMemo(() => {
    return new TypesenseInstantSearchAdapter({
      server: {
        nodes: [
          {
            host: mobileConfigs?.typesense_host_supplierinventories,
            port: 443,
            protocol: "https",
          },
        ],
        apiKey: mobileConfigs?.typesense_api_key_supplierinventories,
      },
      flattenGroupedHits: false,
      additionalSearchParameters: {
        query_by: "rootPartNumber",
        per_page: supplier_discovery_suppliers_limit,
        group_by: "supplierInfo",
        group_limit: supplier_discovery_parts_limit,
        sort_by: "_group_found:desc",
      },
    });
  }, [
    mobileConfigs,
    supplier_discovery_suppliers_limit,
    supplier_discovery_parts_limit,
  ]);

  const getErrorMessage = () => {
    if (validationErrors.length === 0) return "";

    const bulletPoints = validationErrors
      .map((error) => `• ${error.message}`)
      .join("\n\n");

    return `\n${bulletPoints}\n\nPlease contact tech team for assistance.`;
  };

  const getErrorHeader = () => {
    if (validationErrors.length === 0) return "";

    const headers = validationErrors.map((error) => {
      switch (error.type) {
        case ValidationErrorType.DUPLICATE_PARTS:
          return "Part Numbers from same part family detected";
        case ValidationErrorType.PARTS_LIMIT_EXCEEDED:
          return "Parts limit exceeded";
        default:
          return "Validation Error";
      }
    });

    return headers.join(" & ");
  };

  const orderNumberDisplay = orderNumber || orderData?.orderNo || "";

  const handleDownloadBucketAvailabilityPdf = useCallback(async () => {
    try {
      const originalParts = enrichPartQuantitiesWithOrderMrp(
        preFacetedPartNumbers,
        orderItemsForMrpEnrichment ?? orderData?.orderItems,
      );
      const bucket: IBucket = {
        id: BucketType.IN_PROGRESS,
        createdAt: new Date().toISOString(),
        suppliers: selectedSuppliersInBucket,
        originalParts,
      };
      const documentDate = new Date().toLocaleDateString("en-IN", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      });
      await downloadBucketAvailabilityPdf(bucket, orderNumberDisplay, {
        customerName: orderData?.customer?.name ?? "",
        documentDate,
      });
    } catch (error) {
      toast({
        description: "Failed to download PDF. Please try again.",
        status: "error",
        position: "top",
      });
    }
  }, [
    selectedSuppliersInBucket,
    preFacetedPartNumbers,
    orderNumberDisplay,
    orderItemsForMrpEnrichment,
    orderData?.orderItems,
    orderData?.customer?.name,
    toast,
  ]);

  const orderTimestamp = orderData?.orderDate || "N/A";

  const formattedOrderTime = formatDateTime(orderTimestamp);
  const orderDelay = computeDelayFromNow(orderTimestamp);
  const licensePlateNumber = orderData?.licensePlateNumber
    ? String(orderData.licensePlateNumber).toUpperCase()
    : "";
  const requiresEstimate = Boolean(orderData?.requireEstimate);
  const isEstimateOrder =
    requiresEstimate && orderData?._id === orderData?.rootOrderId;

  const customerName = orderData?.customer?.name || "";
  const customerPhoneLink = createPhoneLink(orderData?.customer?.mobileNumber);
  const supplierAssociationsDisplay = useMemo(
    () => formatSupplierAssociationsForDisplay(associations, allSuppliers),
    [associations, allSuppliers],
  );
  const handleCancelOrder = useCallback(
    async (reason: string) => {
      if (!orderData?._id) {
        throw new Error("Order data unavailable");
      }
      await services.cancelGaraazOrder(orderData._id, {
        cancellationReason: reason,
      });
    },
    [orderData?._id],
  );

  const handleHoldOrder = useCallback(async () => {
    if (!orderData?._id) {
      toast({
        description: "Order data unavailable",
        status: "error",
        position: "top",
      });
      return;
    }

    try {
      const currentUTCTimestamp = getCurrentUTCTimestamp();

      await services.checkNextStateOfGaraazOrders(
        orderData._id,
        OrderStatus.CUSTOMER_HOLD,
      );

      if (!orderData?.metaInfo?.[META_INFO_KEYS.CUSTOMER_HOLD_START_TIME]) {
        await services.updateOrderMetaInfo(orderData._id, {
          [META_INFO_KEYS.CUSTOMER_HOLD_START_TIME]: currentUTCTimestamp,
        });
        setOrderMetaInfoWithOrderId(orderData._id, {
          [META_INFO_KEYS.CUSTOMER_HOLD_START_TIME]: currentUTCTimestamp,
        });
      }

      !!refetchAllOrders && refetchAllOrders();
      toast({
        description: "Order put on customer hold successfully",
        status: "success",
        position: "top",
      });

      onSelectionClose();
      onClose();
    } catch (error: any) {
      const errorFromResponse = error?.response?.data?.errorMsg;
      const description =
        errorFromResponse?.[0]?.title ||
        OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;
      toast({
        description: description,
        status: "error",
        position: "top",
      });
    }
  }, [
    orderData?._id,
    orderData?.metaInfo,
    setOrderMetaInfoWithOrderId,
    toast,
    onSelectionClose,
    onClose,
    refetchAllOrders,
  ]);

  // Hooks must stay above the validation early return (Rules of Hooks).
  const partConfirmationNonRemovableSupplierIds = useMemo(() => {
    const ids = new Set<string>();
    partConfirmationResponsesBySupplierId.forEach((_, supplierId) => {
      if (supplierId) ids.add(String(supplierId));
    });
    (partConfirmationSendResults ?? []).forEach((result) => {
      if (result.status === "sent" && result.supplierId) {
        ids.add(String(result.supplierId));
      }
    });
    return Array.from(ids);
  }, [partConfirmationResponsesBySupplierId, partConfirmationSendResults]);

  const generateEstimatePartConfirmationGate = useMemo(
    () =>
      canGenerateEstimateWithPartConfirmation(
        partConfirmationResponsesBySupplierId,
        partConfirmationBucket.length,
      ),
    [partConfirmationResponsesBySupplierId, partConfirmationBucket.length],
  );

  const resolveSupplierDisplayName = useCallback(
    (supplierId: string, fallback?: string) => {
      const match = (allSuppliers || []).find(
        (supplier: any) => String(supplier?._id) === String(supplierId),
      );
      return (
        match?.businessName ||
        match?.supplierName ||
        match?.name ||
        fallback ||
        supplierId
      );
    },
    [allSuppliers],
  );

  const syncBucketsFromPartConfirmations = useCallback(() => {
    const namedSuppliers = (partConfirmationsForOrder?.suppliers ?? []).map(
      (supplier) => ({
        ...supplier,
        supplierName: resolveSupplierDisplayName(
          supplier.supplierId,
          supplier.supplierName,
        ),
      }),
    );
    const { partConfirmationEntries, confirmedSuppliers } =
      hydrateDualBucketsFromPartConfirmations(
        namedSuppliers,
        preFacetedPartNumbers,
      );

    confirmedSuppliers.forEach((supplier) => {
      addSupplierToSelectionBucket(supplier);
    });

    const fullyRespondedIds = new Set(
      namedSuppliers
        .filter((supplier) => {
          const parts = supplier.parts ?? [];
          return (
            parts.length > 0 &&
            parts.every((part) => {
              const status = String(
                (part as { responseStatus?: string; status?: string })
                  .responseStatus ??
                  (part as { status?: string }).status ??
                  "",
              )
                .trim()
                .toLowerCase();
              return status === "confirmed" || status === "declined";
            })
          );
        })
        .map((supplier) => supplier.supplierId),
    );

    setPartConfirmationBucket((prev) => {
      const prevById = new Map(prev.map((entry) => [entry.supplierId, entry]));
      const apiById = new Map(
        partConfirmationEntries.map((entry) => [entry.supplierId, entry]),
      );
      const supplierIds = new Set([
        ...Array.from(prevById.keys()),
        ...Array.from(apiById.keys()),
      ]);

      const next: PartConfirmationBucketEntry[] = [];
      supplierIds.forEach((supplierId) => {
        if (fullyRespondedIds.has(supplierId)) return;
        const reconciled = reconcilePartConfirmationBucketEntry(
          apiById.get(supplierId),
          prevById.get(supplierId),
        );
        if (reconciled) next.push(reconciled);
      });
      return next;
    });
  }, [
    partConfirmationsForOrder,
    preFacetedPartNumbers,
    resolveSupplierDisplayName,
    addSupplierToSelectionBucket,
    setPartConfirmationBucket,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setHasHydratedPartConfirmations(false);
      return;
    }
    if (!isPartConfirmationsFetched) return;

    syncBucketsFromPartConfirmations();
    if (!hasHydratedPartConfirmations) {
      setHasHydratedPartConfirmations(true);
    }
  }, [
    isOpen,
    isPartConfirmationsFetched,
    partConfirmationsForOrder,
    syncBucketsFromPartConfirmations,
    hasHydratedPartConfirmations,
  ]);

  if (validationErrors.length > 0) {
    return (
      <GaraazInfoDialogue
        isOpen={isOpen}
        onOpen={() => {}}
        onClose={onClose}
        leftBtnText=""
        submitBtnText="OK"
        onSubmitBtn={() => {
          onClose();
        }}
        alertBodyText={getErrorMessage()
          .split("\n")
          .map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
        alertGenericHeaderText={getErrorHeader()}
      />
    );
  }
  const handleSelectListItemProxy = (item: any, supplierParts?: any[]) => {
    if (isEstimateOrder) {
      const suppliersPayload = buildSupplierPayloadForDeliveryOptions(
        item,
        supplierParts,
      );
      const launchConfig = {
        orderLabel: orderLabel,
        orderNo: orderNumberDisplay,
        orderId: orderData?._id,
        isSingleGaraazInvoiceRequired: Boolean(
          orderData?.billingDetails?.isSingleGaraazInvoiceRequired,
        ),
        orderData,
        childOrders: childOrdersForEstimateLaunch,
        garaazSupplierIdFromConfig:
          mobileConfigs?.garaaz_supplier_id || "64ae87c051c0be36170dc718",
        isFullyFulfilled: isOrderFullyFulfilled,
        suppliers: suppliersPayload,
        quickEtasMins: mobileConfigs?.quick_etas_mins,
        onConfirm: async (
          selections: NormalizedDeliverySelection[],
          splitData: EnrichedSupplierForDelivery[],
          estimateNo: string,
        ) => {
          const sanitizedSplitData = persistSanitizedDeliveryConfirm(
            selections,
            splitData,
          );
          const cptTime = new Date().toISOString();
          setCptGivenTime(cptTime);
          setSelectedSupplier(item);
          setSelectedSupplierParts(supplierParts);

          setIsSupplierAllocationInProgress(true);
          try {
            await handleSelectListItem(
              item,
              supplierParts || [],
              undefined,
              undefined,
              undefined,
              selections,
              cptTime,
              sanitizedSplitData,
              estimateNo,
              estimateSplitMode,
            );
            onSelectionClose();
          } catch (error: any) {
            const errorFromResponse = error?.response?.data?.errorMsg;
            const description =
              errorFromResponse?.[0]?.title ||
              error?.message ||
              "Failed to process supplier allocation";
            toast({
              description: description,
              status: "error",
              position: "top",
            });
          } finally {
            setIsSupplierAllocationInProgress(false);
            setSelectedSupplier(null);
            setSelectedSupplierParts(null);
            setStoredSelections([]);
            setStoredSplitData([]);
            setCptGivenTime("");
          }
        },
        onClearBucket: clearAllSuppliersFromSelectionBucket,
        onCancelOrder: handleCancelOrder,
        orderCancellationReasons: orderCancellationReasons,
        onHoldOrder: handleHoldOrder,
        refetchOrders: refetchAllOrders,
        onSelectionClose: onSelectionClose,
        onClose: onClose,
        onPartsUnassignedFromEstimate: onPartsUnassignedFromEstimate,
      };

      launchDeliveryOptionsModal(launchConfig);
    } else {
      setSelectedSupplier(item);
      setSelectedSupplierParts(supplierParts);
      setIsTransactionTypeDialogOpen(true);
    }
  };

  const hasConfirmSelections = (
    selections: NormalizedDeliverySelection[],
  ): boolean => {
    return selections.some((s) => s.customerAction === "confirm");
  };

  const handleExecuteBucketProxy = (selectedSuppliers: SelectedSupplier[]) => {
    if (isEstimateOrder) {
      const suppliersPayload =
        buildDeliveryOptionsSuppliersPayload(selectedSuppliers);
      const bucketFullyFulfilled = calculateRemainingParts(
        preFacetedPartNumbers || [],
        selectedSuppliers,
      ).isFullyFulfilled;

      const launchConfig = {
        orderLabel: orderLabel,
        orderNo: orderNumberDisplay,
        orderId: orderData?._id,
        isSingleGaraazInvoiceRequired: Boolean(
          orderData?.billingDetails?.isSingleGaraazInvoiceRequired,
        ),
        orderData,
        childOrders: childOrdersForEstimateLaunch,
        garaazSupplierIdFromConfig:
          mobileConfigs?.garaaz_supplier_id || "64ae87c051c0be36170dc718",
        isFullyFulfilled: bucketFullyFulfilled,
        suppliers: suppliersPayload,
        quickEtasMins: mobileConfigs?.quick_etas_mins,
        onConfirm: async (
          selections: NormalizedDeliverySelection[],
          splitData: EnrichedSupplierForDelivery[],
          estimateNo: string,
        ) => {
          const sanitizedSplitData = persistSanitizedDeliveryConfirm(
            selections,
            splitData,
          );
          const cptTime = new Date().toISOString();
          setCptGivenTime(cptTime);
          setSelectedBucket(selectedSuppliers);

          setIsSupplierAllocationInProgress(true);
          try {
            await handleExecuteSupplierBucket(
              selectedSuppliers,
              undefined,
              undefined,
              undefined,
              selections,
              cptTime,
              sanitizedSplitData,
              estimateNo,
              estimateSplitMode,
            );
            onSelectionClose();
          } catch (error: any) {
            const errorFromResponse = error?.response?.data?.errorMsg;
            const description =
              errorFromResponse?.[0]?.title ||
              error?.message ||
              "Failed to process bucket allocation";
            toast({
              description: description,
              status: "error",
              position: "top",
            });
          } finally {
            setIsSupplierAllocationInProgress(false);
            setSelectedBucket(null);
            setStoredSelections([]);
            setStoredSplitData([]);
            setCptGivenTime("");
          }
        },
        onClearBucket: clearAllSuppliersFromSelectionBucket,
        onCancelOrder: handleCancelOrder,
        orderCancellationReasons: orderCancellationReasons,
        onHoldOrder: handleHoldOrder,
        refetchOrders: refetchAllOrders,
        onSelectionClose: onSelectionClose,
        onClose: onClose,
        onPartsUnassignedFromEstimate: onPartsUnassignedFromEstimate,
      };

      launchDeliveryOptionsModal(launchConfig);
    } else {
      setSelectedBucket(selectedSuppliers);
      setIsExecuteBucketClicked(true);
      setIsTransactionTypeDialogOpen(true);
    }
  };

  /**
   * Dual-bucket (fastest + cheapest) estimate launch for whitelisted enquiries.
   * Each bucket builds its own delivery-options payload (full part allocation),
   * then payloads are concatenated with deliveryType tags. They must not share
   * one remaining-qty clamp — the two buckets are alternative fulfillment paths.
   */
  const handleExecuteBucketsWithDeliveryTypes = (
    buckets: Array<{
      suppliers: SelectedSupplier[];
      deliveryType: EstimateSplitDeliveryType;
    }>,
  ) => {
    const selectedSuppliers = buckets.flatMap((b) => b.suppliers);

    if (!isEstimateOrder) {
      handleExecuteBucketProxy(selectedSuppliers);
      return;
    }

    const suppliersPayload: DeliveryOptionsSupplierInput[] = [];
    for (const { suppliers, deliveryType } of buckets) {
      const bucketPayload = buildDeliveryOptionsSuppliersPayload(suppliers);
      for (const split of bucketPayload) {
        // Skip empty-item splits (API rejects ArrayNotEmpty on items)
        if (!split.items?.length) continue;
        suppliersPayload.push({ ...split, deliveryType });
      }
    }

    if (suppliersPayload.length === 0) {
      toast({
        description: "No supplier splits available for estimate",
        status: "error",
        position: "top",
      });
      return;
    }

    const launchConfig: DeliveryOptionsLaunchConfig = {
      orderLabel: orderLabel,
      orderNo: orderNumberDisplay,
      orderId: orderData?._id,
      isSingleGaraazInvoiceRequired: Boolean(
        orderData?.billingDetails?.isSingleGaraazInvoiceRequired,
      ),
      orderData,
      childOrders: childOrdersForEstimateLaunch,
      garaazSupplierIdFromConfig:
        mobileConfigs?.garaaz_supplier_id || "64ae87c051c0be36170dc718",
      isFullyFulfilled: buckets.some(
        (bucket) =>
          calculateRemainingParts(preFacetedPartNumbers || [], bucket.suppliers)
            .isFullyFulfilled,
      ),
      suppliers: suppliersPayload,
      quickEtasMins: mobileConfigs?.quick_etas_mins,
      onConfirm: async (
        selections: NormalizedDeliverySelection[],
        splitData: EnrichedSupplierForDelivery[],
        estimateNo: string,
      ) => {
        const sanitizedSplitData = persistSanitizedDeliveryConfirm(
          selections,
          splitData,
        );
        const cptTime = new Date().toISOString();
        setCptGivenTime(cptTime);
        setSelectedBucket(selectedSuppliers);

        setIsSupplierAllocationInProgress(true);
        try {
          await handleExecuteSupplierBucket(
            selectedSuppliers,
            undefined,
            undefined,
            undefined,
            selections,
            cptTime,
            sanitizedSplitData,
            estimateNo,
            estimateSplitMode,
          );
          onSelectionClose();
        } catch (error: any) {
          const errorFromResponse = error?.response?.data?.errorMsg;
          const description =
            errorFromResponse?.[0]?.title ||
            error?.message ||
            "Failed to process bucket allocation";
          toast({
            description: description,
            status: "error",
            position: "top",
          });
        } finally {
          setIsSupplierAllocationInProgress(false);
          setSelectedBucket(null);
          setStoredSelections([]);
          setStoredSplitData([]);
          setCptGivenTime("");
        }
      },
      onClearBucket: clearAllSuppliersFromSelectionBucket,
      onCancelOrder: handleCancelOrder,
      orderCancellationReasons: orderCancellationReasons,
      onHoldOrder: handleHoldOrder,
      refetchOrders: refetchAllOrders,
      onSelectionClose: onSelectionClose,
      onClose: onClose,
      onPartsUnassignedFromEstimate: onPartsUnassignedFromEstimate,
    };

    launchDeliveryOptionsModal(launchConfig);
  };

  const handleAddToSupplierSelectionBucket = async (
    item: any,
    supplierParts?: any[],
    options?: AddToSupplierBucketOptions,
  ) => {
    if (item && supplierParts) {
      const supplierCity = supplierCityMap.get(item._id);
      // Tab 1 = Offline Discovery — fetch Typesense stock for inventory snapshot
      const isFromOfflineDiscovery = activeTab === 1;

      let partsToAdd = supplierParts;
      let confirmedByPartId = buildConfirmedByPartId();
      const isEligible = isPartConfirmationEligibleSupplier(
        item._id,
        mobileConfigs?.part_confirmation_eligible_supplier_ids,
      );

      if (isEligible && !isFromOfflineDiscovery && orderNumberDisplay) {
        try {
          const confirmationData = await queryClient.fetchQuery({
            queryKey: [
              ORDER.PART_CONFIRMATION_RESPONSES,
              orderNumberDisplay,
              item._id,
            ],
            queryFn: () =>
              services.getPartConfirmationResponses(
                orderNumberDisplay,
                item._id,
              ),
          });
          confirmedByPartId = buildConfirmedByPartId(
            confirmationData?.parts ?? [],
          );
        } catch {
          confirmedByPartId = buildConfirmedByPartId();
        }
      }

      if (!options?.skipAssignQtyCap && !isFromOfflineDiscovery) {
        partsToAdd = buildDefaultSelectedPartsForBucket(
          remainingPartsToFulfill,
          supplierParts,
          confirmedByPartId,
        );
        if (partsToAdd.length === 0) {
          toast({
            description: "No available parts to add for this supplier",
            status: "warning",
            position: "top",
          });
          return;
        }
      }

      const selectedParts = partsToAdd.map((part: any) => ({
        rootPartNumber: part.rootPartNumber,
        familyQuantity: part.familyQuantity,
        updatedAt: part.updatedAt,
        source: part.source,
        supplierReportedQuantity: part.supplierReportedQuantity,
      }));

      const alreadyInConfirmedBucket = selectedSuppliersInBucket.some(
        (supplier) => String(supplier.supplierId) === String(item._id),
      );
      // Eligible AI → PC. Re-add while already Confirmed → PC (never grow Confirmed).
      const routeToPartConfirmationBucket =
        alreadyInConfirmedBucket || (isEligible && !isFromOfflineDiscovery);

      if (routeToPartConfirmationBucket) {
        const askedParts: PartQuantity[] = selectedParts.flatMap((part) => {
          const original = preFacetedPartNumbers.find(
            (itemPart) => itemPart.rootPartNumber === part.rootPartNumber,
          );
          if (!original) return [];
          return [
            {
              ...original,
              quantity: part.familyQuantity,
            },
          ];
        });
        const confirmationEntry = buildPartConfirmationBucketEntry(
          `${item.supplierName}::${item._id}`,
          askedParts,
          options?.supplierPartsForConfirmationCheck ?? supplierParts,
        );
        if (!confirmationEntry) {
          toast({
            description: "No available parts to add for this supplier",
            status: "warning",
            position: "top",
          });
          return;
        }

        // Incoming = NEW increment from remaining. Merge onto bucket / API
        // pending totals so UI shows 5+1=6; send posts only unsent delta.
        const existingInBucket = partConfirmationBucket.find(
          (entry) => entry.supplierId === item._id,
        );
        const apiPendingBase = buildPartConfirmationEntryFromPendingResponses(
          item._id,
          item.supplierName,
          partConfirmationResponsesBySupplierId.get(item._id),
        );
        const merged = mergePartConfirmationBucketEntries(
          existingInBucket ?? apiPendingBase ?? undefined,
          confirmationEntry,
        );
        const sendableAfterMerge = getSendablePartConfirmationEntries(
          [merged],
          partConfirmationResponsesBySupplierId,
        );
        if (sendableAfterMerge.length === 0) {
          toast({
            description:
              "No new quantity to send for part confirmation for this supplier",
            status: "warning",
            position: "top",
          });
          return;
        }

        setPartConfirmationBucket((prev) => {
          const without = prev.filter((entry) => entry.supplierId !== item._id);
          return [...without, merged];
        });
        return;
      }

      // First-time non-eligible (or offline) → Confirmed bucket.
      const rootPartNumbers = (preFacetedPartNumbers || []).map(
        (part) => part.rootPartNumber,
      );
      const inventoryAvailableByRootPart =
        await buildInventoryAvailableByRootPartFromTypesense({
          typesenseAdapter: typesenseInstantsearchAdapter,
          collectionName: supplierDiscoveryCollectionName,
          supplierId: item._id,
          rootPartNumbers,
          supplierInventoryDeltaQuantityMap,
          supplierPartConfirmationQuantityMap:
            supplierPartConfirmationQuantityMapFromApi,
        });

      addSupplierToSelectionBucket({
        supplierId: item._id,
        supplierName: item.supplierName,
        distanceKm: item.distanceKm,
        supplierCity,
        selectedParts,
        isFromOfflineDiscovery,
        inventoryAvailableByRootPart,
      });
      if (isFromOfflineDiscovery) {
        setActiveTab(0);
      }
    }
  };

  const handleSendPartConfirmationFromBucket = async () => {
    if (sendablePartConfirmationEntries.length === 0) return;
    try {
      const results = await sendPartConfirmationBucket.mutateAsync({
        orderId: orderData?._id ?? orderId,
        orderNo: orderNumberDisplay,
        entries: sendablePartConfirmationEntries,
      });
      setPartConfirmationSendResults(results);
      const sentCount = results.filter(
        (result) => result.status === "sent",
      ).length;
      if (sentCount > 0) {
        queryClient.invalidateQueries({
          queryKey: [
            ORDER.PART_CONFIRMATIONS_FOR_ORDER,
            partConfirmationOrderNo,
          ],
        });
      }
    } catch {
      // errors are surfaced via the mutation's onError toast
    }
  };

  const handleManualSupplierClick = (supplier: any) => {
    setSelectedManualSupplier(supplier);
    setIsPartSelectionModalOpen(true);
  };

  const handlePartSelectionConfirm = async (selectedParts: any[]) => {
    if (selectedManualSupplier && selectedParts.length > 0) {
      const supplierCity = supplierCityMap.get(selectedManualSupplier._id);
      const mappedParts = selectedParts.map((part: any) => ({
        rootPartNumber: part.rootPartNumber,
        familyQuantity: part.quantity,
        updatedAt: Date.now(),
      }));

      const alreadyInConfirmedBucket = selectedSuppliersInBucket.some(
        (supplier) =>
          String(supplier.supplierId) === String(selectedManualSupplier._id),
      );

      // Already in Confirmed → extra qty goes to Part Confirmation, not Confirmed.
      if (alreadyInConfirmedBucket) {
        const askedParts: PartQuantity[] = mappedParts.flatMap((part) => {
          const original = preFacetedPartNumbers.find(
            (itemPart) => itemPart.rootPartNumber === part.rootPartNumber,
          );
          if (!original) return [];
          return [{ ...original, quantity: part.familyQuantity }];
        });
        const confirmationEntry = buildPartConfirmationBucketEntry(
          `${selectedManualSupplier.businessName}::${selectedManualSupplier._id}`,
          askedParts,
          mappedParts,
        );
        if (confirmationEntry) {
          const existingInBucket = partConfirmationBucket.find(
            (entry) => entry.supplierId === selectedManualSupplier._id,
          );
          const apiPendingBase = buildPartConfirmationEntryFromPendingResponses(
            selectedManualSupplier._id,
            selectedManualSupplier.businessName,
            partConfirmationResponsesBySupplierId.get(
              selectedManualSupplier._id,
            ),
          );
          const merged = mergePartConfirmationBucketEntries(
            existingInBucket ?? apiPendingBase ?? undefined,
            confirmationEntry,
          );
          setPartConfirmationBucket((prev) => {
            const without = prev.filter(
              (entry) => entry.supplierId !== selectedManualSupplier._id,
            );
            return [...without, merged];
          });
        }
        setActiveTab(0);
        setIsPartSelectionModalOpen(false);
        setSelectedManualSupplier(null);
        return;
      }

      const rootPartNumbers = (preFacetedPartNumbers || []).map(
        (part) => part.rootPartNumber,
      );
      const inventoryAvailableByRootPart =
        await buildInventoryAvailableByRootPartFromTypesense({
          typesenseAdapter: typesenseInstantsearchAdapter,
          collectionName: supplierDiscoveryCollectionName,
          supplierId: selectedManualSupplier._id,
          rootPartNumbers,
          supplierInventoryDeltaQuantityMap,
          supplierPartConfirmationQuantityMap:
            supplierPartConfirmationQuantityMapFromApi,
        });

      addSupplierToSelectionBucket({
        supplierId: selectedManualSupplier._id,
        supplierName: selectedManualSupplier.businessName,
        distanceKm: undefined,
        supplierCity,
        selectedParts: mappedParts,
        // Part selection modal is only opened from Offline Discovery tab
        isFromOfflineDiscovery: true,
        inventoryAvailableByRootPart,
      });
      setActiveTab(0);
    }
    setIsPartSelectionModalOpen(false);
    setSelectedManualSupplier(null);
  };

  const handlePartSelectionCancel = () => {
    setIsPartSelectionModalOpen(false);
    setSelectedManualSupplier(null);
  };

  const handleTransactionTypeSuccess = async (
    transactionType: string,
    discount?: number,
    creditNotePercentage?: number,
  ) => {
    if (isShareEstimateFlow) {
      setIsSupplierAllocationInProgress(true);
      try {
        await executeShareEstimate();
      } catch {
        // Error already handled in executeShareEstimate
      } finally {
        setIsSupplierAllocationInProgress(false);
        setIsTransactionTypeDialogOpen(false);
        setIsShareEstimateFlow(false);
      }
      return;
    }

    setIsSupplierAllocationInProgress(true);
    try {
      if (isExecuteBucketClicked) {
        if (selectedBucket) {
          await handleExecuteSupplierBucket(
            selectedBucket,
            transactionType,
            creditNotePercentage,
            discount,
            storedSelections.length > 0 ? storedSelections : undefined,
            cptGivenTime || undefined,
            storedSplitData.length > 0 ? storedSplitData : undefined,
            estimateNo || undefined,
            estimateSplitMode,
          );
        }
        setSelectedBucket(null);
        setIsExecuteBucketClicked(false);
        setStoredSelections([]);
        setStoredSplitData([]);
        setCptGivenTime("");
        onSelectionClose();
      } else {
        if (selectedSupplier && selectedSupplierParts) {
          await handleSelectListItem(
            selectedSupplier,
            selectedSupplierParts,
            transactionType,
            creditNotePercentage,
            discount,
            storedSelections.length > 0 ? storedSelections : undefined,
            cptGivenTime || undefined,
            storedSplitData.length > 0 ? storedSplitData : undefined,
            estimateNo || undefined,
            estimateSplitMode,
          );
        }
        setSelectedSupplier(null);
        setSelectedSupplierParts(null);
        setStoredSelections([]);
        setStoredSplitData([]);
        setCptGivenTime("");
        onSelectionClose();
      }
    } finally {
      setIsSupplierAllocationInProgress(false);
      setIsTransactionTypeDialogOpen(false);
    }
  };

  const handleTransactionTypeFail = () => {
    setIsTransactionTypeDialogOpen(false);
    setSelectedSupplier(null);
    setSelectedSupplierParts(null);
    setIsExecuteBucketClicked(false);
    setSelectedBucket(null);
    setIsShareEstimateFlow(false);
    setStoredSelections([]);
    setStoredSplitData([]);
    setCptGivenTime("");
  };

  const openAvailabilityPage = () => {
    try {
      const searchCriteria = {
        parts: preFacetedPartNumbers,
        customerGeoPoint: customerGeoPoint,
        orderNumber: orderNumber,
      };
      const url = `/part-availability?criteria=${encodeURIComponent(
        JSON.stringify(searchCriteria),
      )}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error opening availability page:", error);
    }
  };

  const garaazSupplierIdFromConfig =
    mobileConfigs?.garaaz_supplier_id || "64ae87c051c0be36170dc718";
  const allocationSupplierIdForTransactionModal =
    selectedSupplier?.supplierId ??
    selectedSupplier?._id ??
    (selectedBucket?.some((s) => s.supplierId === garaazSupplierIdFromConfig)
      ? garaazSupplierIdFromConfig
      : selectedBucket?.[0]?.supplierId);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        scrollBehavior="inside"
        isCentered={true}
      >
        <ModalOverlay bg="#fbfbfb" backdropFilter="blur(4px)" />

        <ModalContent
          mx={4}
          marginTop="40px"
          marginBottom="20px"
          borderRadius="xl"
          overflow="hidden"
        >
          <Box
            letterSpacing={"-.25"}
            py={5}
            px={6}
            borderTopRadius="xl"
            bgGradient="linear(to-r, #1976f2, #5b2be0)"
          >
            <HStack justify="space-between" align="start">
              <VStack align="start" spacing={1}>
                <Text
                  fontSize="3xl"
                  fontWeight="900"
                  color="white"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Smart Supplier Selection
                </Text>
                <Text
                  fontSize="0.95rem"
                  color="whiteAlpha.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="500"
                  letterSpacing="-.25px"
                >
                  AI-powered supplier matching for optimal order fulfillment
                </Text>
              </VStack>

              <Box
                backdropFilter="blur(10px)"
                marginRight={10}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                position="relative"
                overflow="hidden"
              >
                <VStack spacing={2} align="center">
                  <Box
                    position="relative"
                    _before={{
                      content: '""',
                      position: "absolute",
                      top: "-4px",
                      left: "-4px",
                      right: "-4px",
                      bottom: "-4px",
                      background:
                        "linear-gradient(45deg, #10B981, #3B82F6, #8B5CF6)",
                      borderRadius: "full",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      zIndex: -1,
                    }}
                  >
                    <IconButton
                      icon={<FaSearch size="1.2em" />}
                      size="lg"
                      bg="whiteAlpha.200"
                      color="white"
                      border="2px solid"
                      borderColor="whiteAlpha.300"
                      _hover={{
                        bg: "whiteAlpha.300",
                        transform: "scale(1.01)",
                        borderColor: "whiteAlpha.400",
                      }}
                      _active={{
                        bg: "whiteAlpha.400",
                        transform: "scale(0.95)",
                      }}
                      aria-label="Check Part Availability Button"
                      onClick={openAvailabilityPage}
                      isDisabled={
                        !preFacetedPartNumbers?.length ||
                        !mobileConfigs?.typesense_host_supplierinventories ||
                        !mobileConfigs?.typesense_api_key_supplierinventories
                      }
                      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    />
                  </Box>

                  <VStack spacing={1} align="center">
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color="whiteAlpha.900"
                      textAlign="center"
                      letterSpacing="tight"
                    >
                      Check Part Availability
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            </HStack>
          </Box>

          <ModalCloseButton
            color="white"
            top="16px"
            right="20px"
            zIndex={1}
            _hover={{
              backdropBlur: 12,
              background: "white",
              color: "blue.500",
            }}
          />

          <ModalBody flex="1" p={0} bg="#F9FAFB" position="relative">
            {typesenseInstantsearchAdapter && (
              <InstantSearch
                key={`main-search-${remainingDiscoverySignature}-${selectedSuppliersInBucket.length}-${partConfirmationBucket.length}-${discoveryExcludedSupplierIds.length}`}
                indexName={supplierDiscoveryCollectionName}
                searchClient={typesenseInstantsearchAdapter.searchClient}
              >
                <Configure filters={optimizedFilter} />
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  alignItems="flex-start"
                  gap={1}
                  bg="#F9FAFB"
                >
                  <Box
                    flex={1}
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    flexDirection="column"
                    h="80vh"
                    maxH="80vh"
                    overflowY="auto"
                    pl={4}
                    pr={4}
                    marginTop={4}
                    paddingBottom={4}
                  >
                    <VStack spacing={4} align="stretch" width="100%">
                      <Box
                        bg="white"
                        borderRadius="xl"
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="sm"
                      >
                        <Text
                          fontSize="0.75rem"
                          fontWeight="800"
                          color="gray.600"
                          textTransform="uppercase"
                          letterSpacing="0.6px"
                        >
                          {orderLabel} Details
                        </Text>
                        <VStack align="stretch" spacing={2.5} mt={2}>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              {orderLabel} No:
                            </Text>
                            <Text
                              fontSize="0.85rem"
                              color="gray.600"
                              wordBreak="break-word"
                            >
                              {orderNumberDisplay || "—"}
                            </Text>
                          </Box>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              {orderLabel} Time:
                            </Text>
                            <Text
                              fontSize="0.85rem"
                              color="gray.600"
                              wordBreak="break-word"
                            >
                              {formattedOrderTime || "—"}
                            </Text>
                          </Box>
                          <Box
                            flex="1"
                            minW={0}
                            display="flex"
                            alignItems="center"
                            gap={2}
                          >
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              Time Elapsed:
                            </Text>
                            <Text
                              fontSize="0.85rem"
                              color="gray.600"
                              wordBreak="break-word"
                            >
                              {orderDelay || "—"}
                            </Text>
                          </Box>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              License Plate No:
                            </Text>
                            <Text
                              fontSize="0.85rem"
                              color="gray.600"
                              wordBreak="break-word"
                            >
                              {licensePlateNumber || "—"}
                            </Text>
                          </Box>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                          >
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              Require Estimate:
                            </Text>
                            <Tag
                              size="sm"
                              colorScheme={
                                requiresEstimate ? "orange" : "green"
                              }
                              variant="subtle"
                              fontWeight="700"
                              textTransform="uppercase"
                            >
                              {requiresEstimate ? "Yes" : "No"}
                            </Tag>
                          </Box>
                        </VStack>
                      </Box>

                      <Box
                        bg="white"
                        borderRadius="xl"
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="sm"
                      >
                        <Text
                          fontSize="0.75rem"
                          fontWeight="800"
                          color="gray.600"
                          textTransform="uppercase"
                          letterSpacing="0.6px"
                        >
                          Customer Details
                        </Text>
                        <VStack align="stretch" spacing={2.5} mt={2}>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              Customer Name:
                            </Text>
                            <Text
                              fontSize="0.85rem"
                              color="gray.600"
                              wordBreak="break-word"
                            >
                              {customerName || "—"}
                            </Text>
                          </Box>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                          >
                            <Text
                              as="span"
                              fontSize="0.85rem"
                              fontWeight="700"
                              color="gray.700"
                              flexShrink={0}
                            >
                              Phone No:
                            </Text>
                            {customerPhoneLink || (
                              <Text fontSize="0.85rem" color="gray.600">
                                —
                              </Text>
                            )}
                          </Box>
                        </VStack>
                      </Box>

                      <Box
                        bg="white"
                        borderRadius="xl"
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="sm"
                      >
                        <Text
                          fontSize="0.75rem"
                          fontWeight="800"
                          color="gray.600"
                          textTransform="uppercase"
                          letterSpacing="0.6px"
                        >
                          Supplier Associations
                        </Text>
                        {supplierAssociationsDisplay.length > 0 ? (
                          <VStack align="stretch" spacing={3} mt={2}>
                            {supplierAssociationsDisplay.map(
                              (association, associationIndex) => (
                                <VStack
                                  key={`supplier-association-${associationIndex}`}
                                  align="stretch"
                                  spacing={2.5}
                                  pt={associationIndex > 0 ? 3 : 0}
                                  borderTop={
                                    associationIndex > 0
                                      ? "1px solid"
                                      : undefined
                                  }
                                  borderColor="gray.100"
                                >
                                  {SUPPLIER_ASSOCIATION_DISPLAY_FIELDS.map(
                                    (field) => (
                                      <Box
                                        key={`${associationIndex}-${field}`}
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={1}
                                        alignItems="baseline"
                                      >
                                        <Text
                                          as="span"
                                          fontSize="0.85rem"
                                          fontWeight="700"
                                          color="gray.700"
                                        >
                                          {field}:
                                        </Text>
                                        <Text
                                          fontSize="0.85rem"
                                          color="gray.600"
                                          wordBreak="break-word"
                                        >
                                          {association[field] || "—"}
                                        </Text>
                                      </Box>
                                    ),
                                  )}
                                </VStack>
                              ),
                            )}
                          </VStack>
                        ) : (
                          <Text fontSize="0.85rem" color="gray.600" mt={2}>
                            —
                          </Text>
                        )}
                      </Box>

                      <RadiusFilter
                        setGeoDistanceFilter={setGeoDistanceFilter}
                        maxKm={maxKm}
                        customerGeoPoint={customerGeoPoint}
                      />
                      <PartsBreakdownPanel
                        selectedSuppliers={selectedSuppliersInBucket}
                        remainingParts={remainingPartsToFulfill}
                        originalParts={preFacetedPartNumbers}
                      />
                    </VStack>
                  </Box>
                  <TabbedSupplierList
                    customerSupplierAssociations={customerSupplierAssociations}
                    customerGeoPoint={customerGeoPoint}
                    preFacetedPartNumbers={preFacetedPartNumbers}
                    remainingParts={remainingPartsToFulfill}
                    handleSelectListItem={(item: any, supplierParts?: any[]) =>
                      isOrderFullyFulfilled
                        ? null
                        : handleSelectListItemProxy(item, supplierParts)
                    }
                    handleAddToSelectedSupplierBucket={(
                      item: any,
                      supplierParts?: any[],
                      options?: AddToSupplierBucketOptions,
                    ) =>
                      isOrderFullyFulfilled
                        ? null
                        : handleAddToSupplierSelectionBucket(
                            item,
                            supplierParts,
                            options,
                          )
                    }
                    onSelectionClose={() => {}}
                    typesenseInstantsearchAdapter={
                      typesenseInstantsearchAdapter
                    }
                    geoDistanceFilter={geoDistanceFilter}
                    supplierDiscoveryCollectionName={
                      supplierDiscoveryCollectionName
                    }
                    supplier_discovery_parts_limit={
                      supplier_discovery_parts_limit
                    }
                    selectedSuppliers={selectedSuppliersInBucket}
                    excludeSupplierIds={discoveryExcludedSupplierIds}
                    isFullyFulfilled={isOrderFullyFulfilled}
                    isBucketSaveAllowed={isBucketSaveAllowed}
                    onSaveBucket={handleSaveCurrentBucket}
                    setShowCreateAnotherBucket={setShowCreateAnotherBucket}
                    supplierCityMap={supplierCityMap}
                    filteredSuppliers={filteredSuppliers}
                    onSelectManualSupplier={handleManualSupplierClick}
                    isEstimateOrder={isEstimateOrder}
                    onAddToBroadcast={handleAddToBroadcast}
                    broadcastSuppliers={broadcastSuppliers}
                    isSendingBroadcast={isSendingBroadcast}
                    isBroadcastEnabled={isBroadcastEnabled}
                    notificationRequestStatusBySupplier={
                      notificationRequestStatusBySupplier
                    }
                    offlinePartAvailabilityDiscrepanciesPerSupplierMap={
                      offlinePartAvailabilityDiscrepanciesPerSupplierMap
                    }
                    supplierInventoryDeltaQuantityMap={
                      supplierInventoryDeltaQuantityMap
                    }
                    supplierPartConfirmationQuantityMap={
                      supplierPartConfirmationQuantityMap
                    }
                    partConfirmationBucket={partConfirmationBucket}
                    activeTab={activeTab}
                    onActiveTabChange={setActiveTab}
                    orderId={orderData?._id ?? orderId}
                    orderNo={orderNumberDisplay}
                  />
                  <Box
                    flex={1}
                    h="80vh"
                    maxH="80vh"
                    overflowY="auto"
                    pl={4}
                    pb={4}
                  >
                    <SelectedSuppliersPanel
                      selectedSuppliers={selectedSuppliersInBucket}
                      onRemoveSupplier={removeSupplierFromSelectionBucket}
                      onClearAll={clearAllSuppliersFromSelectionBucket}
                      isFullyFulfilled={isOrderFullyFulfilled}
                      fulfillmentPercentage={orderFulfillmentPercentage}
                      orderNumber={orderNumber}
                      originalParts={preFacetedPartNumbers}
                      orderData={orderData}
                      handleExecuteBucket={handleExecuteBucketProxy}
                      handleExecuteBucketsWithDeliveryTypes={
                        handleExecuteBucketsWithDeliveryTypes
                      }
                      showCreateAnotherBucket={showCreateAnotherBucket}
                      remainingPartsToFulfill={remainingPartsToFulfill}
                      isFetchingDeliveryOptions={isFetchingDeliveryOptions}
                      isEstimateOrder={isEstimateOrder}
                      onDownloadBucketPdf={handleDownloadBucketAvailabilityPdf}
                      isDownloadBucketPdfDisabled={
                        preFacetedPartNumbers.length === 0
                      }
                      isGenerateEstimateBlockedByPartConfirmation={
                        !generateEstimatePartConfirmationGate.allowed
                      }
                      partConfirmationBlockReason={
                        generateEstimatePartConfirmationGate.reason
                      }
                      partConfirmationBucket={partConfirmationBucket}
                      onRemovePartConfirmationSupplier={
                        removeSupplierFromPartConfirmationBucket
                      }
                      onSendPartConfirmation={
                        handleSendPartConfirmationFromBucket
                      }
                      isSendingPartConfirmation={
                        sendPartConfirmationBucket.isLoading
                      }
                      isSendPartConfirmationEnabled={
                        sendablePartConfirmationEntries.length > 0
                      }
                      sendPartConfirmationDisabledReason={
                        "Already sent for these quantities — add a new supplier or extra qty"
                      }
                      partConfirmationSendResults={partConfirmationSendResults}
                      partConfirmationNonRemovableSupplierIds={
                        partConfirmationNonRemovableSupplierIds
                      }
                    />
                    {isBroadcastEnabled && broadcastSuppliers.length > 0 && (
                      <BroadcastSuppliersPanel
                        broadcastSuppliers={broadcastSuppliers}
                        onRemoveSupplier={removeSupplierFromBroadcast}
                        onClearAll={clearBroadcastSuppliers}
                        onSendBroadcastQuery={handleSendBroadcastQuery}
                        isSending={isSendingBroadcast}
                      />
                    )}
                  </Box>
                </Box>
              </InstantSearch>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      {DeliveryOptionsModal}
      {/* Estimate modal: transaction type configured via PATCH split
      <MultiSplitTransactionTypeModal
        isOpen={isMultiSplitTransactionTypeModalOpen}
        supplierSplits={multiSplitTransactionTypeModalSuppliers}
        orderData={orderData}
        garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
        onConfirm={handleMultiSplitTransactionTypeModalConfirm}
        onCancel={handleMultiSplitTransactionTypeModalCancel}
      />
      */}
      <TransactionTypeSelectComponent
        isOpen={isTransactionTypeDialogOpen}
        onClose={() => {
          setIsTransactionTypeDialogOpen(false);
          setSelectedSupplier(null);
          setSelectedSupplierParts(null);
          setIsExecuteBucketClicked(false);
          setIsShareEstimateFlow(false);
        }}
        onSuccess={handleTransactionTypeSuccess}
        onFail={handleTransactionTypeFail}
        supplierName={selectedSupplier?.supplierName}
        supplierId={allocationSupplierIdForTransactionModal}
        garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
        orderId={orderId}
        setTransactionType={setTransactionType}
        orderData={orderData}
        isLoading={isSupplierAllocationInProgress}
      />
      <GaraazLoadingModal
        isOpen={isSupplierBucketExecuting}
        title="Executing bucket"
        message="Please do not close the window — operation in progress."
      />
      <PartSelectionModal
        isOpen={isPartSelectionModalOpen}
        onClose={handlePartSelectionCancel}
        onConfirm={handlePartSelectionConfirm}
        availableParts={remainingPartsToFulfill}
        supplierName={
          selectedManualSupplier?.businessName || "Selected Supplier"
        }
      />
    </>
  );
};

export default SupplierInventorySearch;
